import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Loader2 } from "lucide-react";
import { api, useAuth, withRefresh } from "@/lib/auth";
import { drawCertificate, masteryDate, saveCertificate } from "@/lib/certificate";
import { Reveal } from "./Reveal";

export default function Certificate({ data }) {
  const { user } = useAuth();
  const canvas = useRef(null);
  const [name, setName] = useState(user?.name ?? "");
  const [src, setSrc] = useState("");
  const [mail, setMail] = useState(undefined);
  const date = masteryDate(data);
  useEffect(() => {
    if (!user?.initiate) return undefined;
    let alive = true;
    withRefresh(() => api.get("/mastery"))
      .then(({ data: d }) => alive && setMail(d?.email ?? null))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [user?.initiate]);
  useEffect(() => {
    if (mail !== null || !src || !canvas.current) return;
    setMail("pending");
    saveCertificate(canvas.current).catch(() => setMail("failed"));
  }, [mail, src]);
  useEffect(() => {
    if (user?.name) setName((n) => n || user.name);
  }, [user?.name]);

  useEffect(() => {
    if (!user?.initiate || !canvas.current) return undefined;
    let alive = true;
    const t = setTimeout(async () => {
      await drawCertificate(canvas.current, { name: name.trim() || "Beloved Initiate", date });
      if (alive) setSrc(canvas.current.toDataURL("image/jpeg", 0.6));
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [name, date, user?.initiate]);

  if (!user?.initiate) {
    return (
      <div data-testid="certificate-locked" className="mt-14 border border-[#D9B47A]/25 p-8">
        <p className="font-display text-2xl">Your Certificate of Mastery is kept for initiates.</p>
        <Link to="/initiate" className="mt-4 inline-block text-[#D9B47A] underline">Become an initiate</Link>
      </div>
    );
  }

  const download = () => {
    saveCertificate(canvas.current).catch(() => {});
    canvas.current.toBlob((blob) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "Tantric-Temple-Certificate-of-Mastery.png";
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    }, "image/png");
  };

  return (
    <Reveal>
      <section data-testid="certificate-section" className="mt-14 border border-[#D9B47A]/30 bg-[#0D0908] p-6 sm:p-10">
        <p className="font-tt-mono text-[12px] uppercase tracking-[0.35em] text-[#D9B47A]">Your Certificate of Mastery</p>
        <p className="mt-3 font-display text-3xl sm:text-4xl leading-tight">All thirteen gates, <em className="text-[#D9B47A]">walked.</em></p>
        <canvas ref={canvas} className="hidden" />
        <div className="mt-8 aspect-[3508/2480] w-full border border-[#D9B47A]/20 bg-[#0A0706] flex items-center justify-center">
          {src ? <img data-testid="certificate-preview" src={src} alt="Certificate of Mastery preview" className="w-full h-full object-cover" /> : <Loader2 className="w-6 h-6 animate-spin text-[#D9B47A]" />}
        </div>
        <div className="mt-8 flex flex-col sm:flex-row gap-4 sm:items-end">
          <label className="flex-1">
            <span className="font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#A8998C]">Name on your certificate</span>
            <input data-testid="certificate-name" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="mt-2 w-full bg-transparent border-b border-[#D9B47A]/30 py-3 text-lg placeholder:text-[#8F8176] focus:outline-none focus:border-[#D9B47A] transition-colors" />
          </label>
          <button data-testid="certificate-download" disabled={!src} onClick={download} className="inline-flex items-center justify-center gap-3 rounded-full bg-[#D9B47A] text-[#0A0706] px-7 py-4 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors disabled:opacity-50">
            <Download className="w-4 h-4" /> Download PNG
          </button>
        </div>
        {(mail === "pending" || mail === "sent") && <p data-testid="certificate-email-note" className="mt-5 text-sm text-[#D9B47A]">A celebration letter from Goddess Ariel, with your certificate, has been sent to {user.email}.</p>}
      </section>
    </Reveal>
  );
}
